import { useEffect, useRef, useState } from "react";
import type {
  ResourceDeletePropagation,
  ResourceDeleteResponse,
  ResourceDetailResponse,
  ResourceOperationResponse,
} from "@k8s-dashboard/contracts";
import {
  CHANGE_UNKNOWN_MESSAGE,
  changeErrorMessage,
  fetchOperation,
  isUnknownResult,
  OPERATION_STATE_TEXT,
  OPERATION_UNKNOWN_STATE_TEXT,
  newCorrelationId,
  requestIdOf,
  submitDelete,
} from "./change";
import { fixedTextFor } from "./dryrun";

/**
 * 삭제 (ADR 0019 Phase 2 결정 5)
 * --------------------------------------------------------------------------
 * 되돌릴 수 없는 유일한 화면입니다. 그래서 세 가지를 요구합니다.
 *
 *  1. **이름 재입력.** 대상 이름을 손으로 정확히 다시 씁니다.
 *  2. **전파 정책을 눈에 보이게.** 서버가 기본값을 몰래 고르지 않으므로 화면도
 *     고르지 않습니다 — 무엇이 선택되었는지 항상 보입니다.
 *  3. **UID/resourceVersion precondition.** 상세가 서버에서 받아 온 값을 그대로
 *     보냅니다. 그 사이에 객체가 바뀌면 API 서버가 막습니다.
 *
 * force·gracePeriodSeconds 컨트롤은 없습니다. 계약에 필드가 없고 여기서도
 * 만들지 않습니다.
 */

type Phase = "idle" | "submitting" | "deleted" | "error" | "unknown";

/** 계약 enum과 화면 설명입니다. 셋뿐이고 순환하지 않습니다. */
const POLICIES: { value: ResourceDeletePropagation; label: string; detail: string }[] = [
  { value: "Foreground", label: "Foreground", detail: "하위 객체를 먼저 지우고 마지막에 이 객체를 지웁니다." },
  { value: "Background", label: "Background", detail: "이 객체를 먼저 지우고 하위 객체는 뒤에서 정리합니다." },
  { value: "Orphan", label: "Orphan", detail: "하위 객체를 남깁니다. 남은 객체는 소유자가 없어집니다." },
];

const OUTCOME_TEXT: Record<string, string> = {
  deleted: "삭제되었습니다.",
  finalizing: "삭제 요청은 수락되었지만 finalizer가 남아 객체가 아직 있습니다.",
  unconfirmed: "삭제 요청은 전달되었지만 결과를 확인하지 못했습니다.",
};

const UNKNOWN_OUTCOME_TEXT = "서버가 이 화면이 모르는 결과로 답했습니다.";

export interface DeletePanelProps {
  detail: ResourceDetailResponse;
  /** 삭제가 끝나면 상위가 목록을 다시 조회하고 드로어를 닫습니다. */
  onDeleted: (result: ResourceDeleteResponse) => void;
}

export function DeletePanel({ detail, onDeleted }: DeletePanelProps) {
  const [confirmName, setConfirmName] = useState("");
  const [policy, setPolicy] = useState<ResourceDeletePropagation>("Foreground");
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<ResourceDeleteResponse | undefined>(undefined);
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [operation, setOperation] = useState<ResourceOperationResponse | undefined>(undefined);
  const [checking, setChecking] = useState(false);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    setConfirmName("");
    setPhase("idle");
    setMessage(undefined);
    setResult(undefined);
    setRequestId(undefined);
    setOperation(undefined);
  }, [detail.uid, detail.resourceVersion]);

  const matches = confirmName === detail.name;

  const onDelete = async () => {
    if (!matches || phase === "submitting") return;
    setPhase("submitting");
    setMessage(undefined);
    /* 적용과 같은 이유로 상관 ID를 **보내기 전에** 만듭니다. (change.ts) */
    const correlationId = newCorrelationId();
    setRequestId(correlationId);
    try {
      const deleted = await submitDelete({ detail, confirmName, propagationPolicy: policy, requestId: correlationId });
      if (!aliveRef.current) return;
      setResult(deleted);
      setRequestId(deleted.requestId || correlationId);
      setPhase("deleted");
      onDeleted(deleted);
    } catch (error) {
      if (!aliveRef.current) return;
      setRequestId(requestIdOf(error) ?? correlationId);
      if (isUnknownResult(error)) {
        setMessage(CHANGE_UNKNOWN_MESSAGE);
        setPhase("unknown");
        return;
      }
      setMessage(changeErrorMessage(error));
      setPhase("error");
    }
  };

  const onCheck = async () => {
    if (!requestId || checking) return;
    setChecking(true);
    try {
      const status = await fetchOperation({ clusterId: detail.clusterId, requestId });
      if (aliveRef.current) setOperation(status);
    } catch (error) {
      if (aliveRef.current) setMessage(changeErrorMessage(error));
    } finally {
      if (aliveRef.current) setChecking(false);
    }
  };

  return (
    <section className="resource-delete" aria-label="삭제">
      <p className="resource-delete__warning" role="note">
        <span className="resource-review__glyph" aria-hidden="true">
          !
        </span>
        <strong>되돌릴 수 없습니다.</strong> 강제 삭제(gracePeriodSeconds=0)는 지원하지 않으며, 그
        사이에 객체가 바뀌면 서버가 삭제를 거절합니다.
      </p>

      <fieldset className="resource-delete__policies">
        <legend className="resource-review__label">전파 정책</legend>
        {POLICIES.map((option) => (
          <label key={option.value} className="resource-delete__policy">
            <input
              type="radio"
              name="resource-delete-policy"
              value={option.value}
              checked={policy === option.value}
              disabled={phase === "submitting" || phase === "deleted"}
              onChange={() => setPolicy(option.value)}
            />
            <span className="resource-delete__policy-label">{option.label}</span>
            <span className="muted">{option.detail}</span>
          </label>
        ))}
      </fieldset>

      <label className="resource-apply__confirm" htmlFor="resource-delete-confirm">
        <span className="resource-review__label">
          삭제하려면 대상 이름 <code className="ds-ident">{detail.name}</code>을(를) 입력하세요
        </span>
        <input
          id="resource-delete-confirm"
          type="text"
          className="resource-apply__input"
          value={confirmName}
          autoComplete="off"
          spellCheck={false}
          maxLength={253}
          disabled={phase === "submitting" || phase === "deleted"}
          onChange={(e) => setConfirmName(e.target.value)}
        />
      </label>

      <div className="resource-review__actions">
        <button
          type="button"
          className="ds-button ds-button--sm ds-button--destructive"
          disabled={!matches || phase === "submitting" || phase === "deleted"}
          onClick={() => void onDelete()}
        >
          {phase === "submitting" ? "삭제하는 중…" : "삭제"}
        </button>
        {phase === "submitting" && (
          <span className="muted" role="status">
            제출했습니다. 이 요청은 취소할 수 없습니다.
          </span>
        )}
      </div>

      {phase === "deleted" && result && (
        <div className="state state--applied" role="status">
          <span className="state__glyph" aria-hidden="true">
            ✓
          </span>
          <span className="state__title">{fixedTextFor(OUTCOME_TEXT, result.outcome) ?? UNKNOWN_OUTCOME_TEXT}</span>
          {result.outcome === "finalizing" && (
            <span className="state__detail">남은 finalizer {result.finalizerCount}개가 끝나야 사라집니다.</span>
          )}
        </div>
      )}

      {(phase === "error" || phase === "unknown") && (
        <div className={phase === "unknown" ? "state state--forbidden" : "state state--error"} role="alert">
          <span className="state__glyph" aria-hidden="true">
            {phase === "unknown" ? "?" : "✕"}
          </span>
          <span className="state__title">
            {phase === "unknown" ? "결과를 확인하지 못했습니다" : "삭제하지 못했습니다"}
          </span>
          <span className="state__detail">{message}</span>
          {requestId && (
            <span className="resource-apply__request muted">
              요청 ID <span className="ds-ident">{requestId}</span>
            </span>
          )}
        </div>
      )}

      {phase === "unknown" && requestId && (
        <div className="resource-review__actions">
          <button type="button" className="ds-button ds-button--sm" disabled={checking} onClick={() => void onCheck()}>
            {checking ? "확인하는 중…" : "작업 상태 확인"}
          </button>
          {operation && (
            <span className="resource-apply__operation" role="status">
              {fixedTextFor(OPERATION_STATE_TEXT, operation.state) ?? OPERATION_UNKNOWN_STATE_TEXT}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
